import React from "react"
import { useNavigate } from "react-router-dom"

import { useAlert, useAuth } from "hooks"
import * as BikeApi from "features/bike/api/BikeApi"

import { getEntityId, getOldStoreId } from "utils/user"
import { normalizeBoolean } from "utils/functions"
import {
	BIKE_BACK_STORE,
	BIKE_STOLEN,
	INTER_CHAIN,
	operatorList,
	MFC_OPERATOR,
	bikeTypes,
	bikeBrands,
	ARCADE_STOREID,
	ARCADE_STOREID_ST,
	REQUEST_ONGOING,
} from "utils/constants"
import { Bike, BikeDescription, ErrorResponse, LocalBike } from "models"

const MF_SUFFIXE_ELECTRIQUE = "ELECTRIQUE"

export enum BikeProcessActionType {
	CREATE_BIKE = "CREATE_BIKE",
	UPDATE_BIKE = "UPDATE_BIKE",
	SET_STATUS = "SET_STATUS",
	SET_IS_SELLER = "SET_IS_SELLER",
	SET_SHOW_OWNER = "SET_SHOW_OWNER",
	SET_IS_WALLET_SELLER = "SET_IS_WALLET_SELLER",
	RESET_DATA = "RESET_DATA",
}

interface BikeProcessAction {
	type: BikeProcessActionType
	payload?: Partial<Bike> | boolean | number
}

interface BikeProcessState {
	initialBike: Bike
	currentBike: LocalBike
	bikeStatus: number
	showOwner: boolean
	isSeller: boolean
	isWalletSeller: boolean
}

type BikeProcessContextInterface = {
	bikeProcessState: BikeProcessState
	bikeDispatch: React.Dispatch<BikeProcessAction>

	setInitialBike: (bike: Bike) => void
	setCurrentBike: (bike: LocalBike) => void
	setShowOwner: (show: boolean) => void
	resetBikeStoreInfo: () => void

	fetchBike: (slug: string) => void
}

// We have 2 states for one bike
// initialBike = it's the bike from API. We need to "prepare" it.
// And with initialBike's information, we can set data to bikeStatus, isSeller, showOwner and isWalletSeller
// currentBike = it's the bike displayed on application

const BikeStoreContext = React.createContext<BikeProcessContextInterface | undefined>(undefined)

// const useBikeStore = () => React.useContext(BikeStoreContext)

/**********************
 ** Reducer **
 **********************/

const bikeProcessReducer = (bikeProcessState: BikeProcessState, action: BikeProcessAction) => {
	const { type, payload } = action
	switch (type) {
		case BikeProcessActionType.CREATE_BIKE: {
			return {
				...bikeProcessState,
				currentBike: {
					...bikeProcessState.currentBike,
					...(payload as Partial<Bike>),
				},
			}
		}
		case BikeProcessActionType.UPDATE_BIKE: {
			return {
				...bikeProcessState,
				currentBike: {
					...bikeProcessState.currentBike,
					...(payload as Partial<LocalBike>),
				},
			}
		}
		case BikeProcessActionType.SET_STATUS: {
			return {
				...bikeProcessState,
				bikeStatus: payload as number,
			}
		}
		case BikeProcessActionType.SET_IS_SELLER: {
			return {
				...bikeProcessState,
				isSeller: payload as boolean,
			}
		}
		case BikeProcessActionType.SET_SHOW_OWNER: {
			return {
				...bikeProcessState,
				showOwner: payload as boolean,
			}
		}
		case BikeProcessActionType.SET_IS_WALLET_SELLER: {
			return {
				...bikeProcessState,
				isWalletSeller: payload as boolean,
			}
		}
		case BikeProcessActionType.RESET_DATA: {
			return {
				...bikeProcessState,
				initialBike: null,
				currentBike: null,
				bikeStatus: null,
				showOwner: false,
				isSeller: true,
				isWalletSeller: false,
			}
		}
		default: {
			throw new Error(`Unhandled action type: ${type}`)
		}
	}
}

/**********************
 ** Context provider **
 **********************/

const BikeStoreProvider = ({ children }: { children: React.ReactNode }) => {
	const [bikeProcessState, bikeDispatch] = React.useReducer(bikeProcessReducer, {
		initialBike: null,
		currentBike: null,
		bikeStatus: null,
		isSeller: true, // is the user the seller of the given bike ?
		showOwner: false,
		// un wallet seller est un loueur de vélo via le wallet. Il utilise le store pour revendre les vélos qui lui appartiennent (consumerId === walletId)
		isWalletSeller: false,
	})

	const { alert } = useAlert()
	const { entity } = useAuth()
	const { logout } = useAuth()
	const navigate = useNavigate()

	// or is the user an admin ?
	const isBO = entity?.entity_data?.entity_permission?.role === "MANAGEENTITIES"

	const setInitialBike = (bike: Bike) => {
		const newBike = preparedBike(bike)
		bikeDispatch({ type: BikeProcessActionType.CREATE_BIKE, payload: newBike })
	}

	const setCurrentBike = (bike: LocalBike) => {
		bikeDispatch({ type: BikeProcessActionType.UPDATE_BIKE, payload: bike })
	}

	const setShowOwner = (show: boolean) => {
		bikeDispatch({ type: BikeProcessActionType.SET_SHOW_OWNER, payload: show })
	}

	const resetBikeStoreInfo = () => {
		bikeDispatch({ type: BikeProcessActionType.RESET_DATA })
	}

	const setBikeStatus = (status: number) => {
		bikeDispatch({ type: BikeProcessActionType.SET_STATUS, payload: status })
	}

	const setIsSeller = (isSeller: boolean) => {
		bikeDispatch({ type: BikeProcessActionType.SET_IS_SELLER, payload: isSeller })
	}

	const setIsWalletSeller = (isWalletSeller: boolean) => {
		bikeDispatch({ type: BikeProcessActionType.SET_IS_WALLET_SELLER, payload: isWalletSeller })
	}

	const fetchBike = async (slug: string) => {
		try {
			const { res: getBike, getError } = await BikeApi.getBike(slug, "byBikeId")
			if (getError()) throw getError()
			setInitialBike(getBike)
		} catch (error) {
			console.error(error)
			const erroMsg = (error as ErrorResponse)?.message?.toString()
			if (erroMsg) alert("error", erroMsg)

			if (
				["BikeNotFoundException", "InvalidArgument", "EntityStatusException"].includes(
					(error as ErrorResponse)?.code
				)
			) {
				bikeProcessState.showOwner ? setShowOwner(false) : navigate(-1)
			}
			if (["NotAuthorizedException"].includes((error as ErrorResponse)?.code)) logout()
		}
	}

	const preparedBike = (getBike: Partial<Bike>) => {
		const loadedBikeBasis = {
			code: getBike.bike_id || "",
			operator: getBike.operator || "",
			account_type: getBike.account_type || "",
			account_id: getBike.account_id || "",
			identifier:
				operatorList.find((oneOperator) => oneOperator.id === getBike.operator)?.logo || "",
			status: getBike.bike_status || null,
			row_version: getBike.row_version || "",
			ocode_id: getBike.ocode_id || "",
			bike_description_manufacturer_id: getBike.bike_description_manufacturer_id || "",
			description_made_by: getBike?.description_made_by || "",
			bikeSeller: getBike?.bike_last_registered_by?.social_reason || "",
			bike_owner: getBike?.bike_owner || null,
			bike_last_registered_by: getBike?.bike_last_registered_by || null,
			property_transfer_request: getBike?.property_transfer_request || null,
			last_event_ocodepro: getBike?.extra_data?.last_event_ocodepro || null,
			mail_verified: getBike?.mail_verified || false,
		}

		const loadedBikeDescription: BikeDescription = {
			brand: getBike?.bike_description?.brand || "",
			brand_id: getBike?.bike_description?.brand_id || "",
			model: getBike?.bike_description?.model || "",
			gear_type_id: getBike?.bike_description?.gear_type_id || "",
			color: getBike?.bike_description?.color || "",
			frame_serial_number: getBike?.bike_description?.frame_serial_number || "",
			...(() => {
				let tabGearType = getBike?.bike_description?.gear_type?.split(" ") || ""
				let isVAE = ""

				if (getBike?.bike_description) {
					isVAE = getBike?.bike_description?.is_electric
						? String(getBike?.bike_description?.is_electric)
						: "false"
					if (getBike?.bike_description?.is_electric) {
						isVAE = String(getBike?.bike_description?.is_electric)
					} else if (tabGearType) {
						// we check if electric when back to store from IN
						isVAE = String(
							tabGearType[tabGearType.length - 1] === MF_SUFFIXE_ELECTRIQUE
						)
					} else {
						isVAE = "false"
					}
				}

				if (normalizeBoolean(isVAE))
					tabGearType = Array.isArray(tabGearType)
						? tabGearType.filter((e) => e !== MF_SUFFIXE_ELECTRIQUE)
						: tabGearType

				// Does gearType and brand already exist in our lists ?
				let gearTypeFind
				let brandFind

				if (getBike?.bike_description?.gear_type_id)
					gearTypeFind =
						bikeTypes.find(
							(b) => b.value === getBike?.bike_description?.gear_type_id
						) || bikeTypes[0]

				if (getBike?.bike_description?.brand)
					brandFind =
						bikeBrands.find(
							(b) =>
								b.label.toLowerCase() ===
								getBike?.bike_description?.brand.toLowerCase()
						) || bikeBrands[0]

				return {
					isVAE: isVAE,
					gear_type:
						gearTypeFind && gearTypeFind.value !== 999
							? gearTypeFind.label
							: getBike?.bike_description?.gear_type,
					gear_type_id: gearTypeFind
						? gearTypeFind.value
						: getBike?.bike_description?.gear_type_id,
					brand:
						brandFind && brandFind.value !== 999
							? brandFind.label
							: getBike?.bike_description?.brand,
					brand_id: brandFind ? brandFind.value : "",
					...(normalizeBoolean(isVAE)
						? {
								engine_serial_number:
									getBike?.bike_description?.engine_serial_number,
								battery_serial_number:
									getBike?.bike_description?.battery_serial_number,
							}
						: {}),
				}
			})(),
		}

		const loadedBikeSoldInfos = {
			// status === 6 --> retour magasin
			storeId:
				getBike.bike_status === BIKE_BACK_STORE
					? getBike.bike_owner_store_id
					: getBike.bike_last_registered_by?.store_id,
			...(getBike.bike_status === 1 || isBO
				? {
						// sold ?
						accountActivated: getBike?.mail_verified,
					}
				: {}),
			// ...(getBike.bike_status === 5 || getBike.bike_status === 2
			// 	? {
			// 			// in sale by owner
			// 			owner: {
			// 				mail: getBike?.bike_owner?.mail,
			// 			},
			// 	  }
			// 	: {}),
		}

		//Bike Status
		const bikeStatus = getBike.bike_status
		//RequestStatus
		const PropertiyrequestStatusBike = getBike?.property_transfer_request?.status
		//Get Account id into Operator
		const operatorAccountID = entity?.data_operators?.find(
			(operator) => operator.operator === getBike?.operator
		)?.entity_data?.account_id

		// Set local states
		setBikeStatus(getBike.bike_status)
		setCurrentBike({
			...loadedBikeBasis,
			...loadedBikeDescription,
			...loadedBikeSoldInfos,
		})

		// Has the current store (user) already seen/used/sold this bike ?
		// retour magasin status = 6 et le magasin n'est pas celui qui a vendu le vélo : on affiche "ce vélo n'est pas disponible à la vente"
		// status !== 6 et le magasin n'est pas celui qui a vendu le vélo : on affiche les infos vélos avec mail caché (écran Louise)
		if (getBike.bike_status !== null) {
			const bikeStoreId = loadedBikeSoldInfos.storeId
			const storeStoreId = "ST-" + getEntityId()
			const oldStoreId = getOldStoreId()

			// Un vendeur MFC avec un gender Intersport doit pouvoir revendre un vélo MFC status 6 d'un autre vélociste
			const isMFCIntersportSeller = entity?.entity_data?.code_chaine === INTER_CHAIN
			const isMFCBike = getBike?.operator === MFC_OPERATOR

			// Implémentation de 2 fixes pour Arcade :
			// 1 - Si le vélo n'est pas volé, on regarde s'il provient du Wallet associé au magasin. Si c'est le cas il peut
			//     vendre ce vélo (Arcade livre parfois des vélos par flotte et ceux-ci sont donc gérés depuis le wallet).
			// 2 - Si le vélo est de retour en magasin (statut 6) et que son storeId est celui d'Arcade, alors le magasin
			//     peut vendre le vélo comme s'il lui appartenait
			// Attention Arcade a 2 storeId différents qu'il faut bien prendre en compte ARCADE_STOREID || ARCADE_STOREID_ST
			if (entity.entity_data?.wallet_id !== getBike.consumer_id) setIsWalletSeller(false)
			if (
				getBike.bike_status !== BIKE_STOLEN &&
				entity.entity_data?.wallet_id === getBike.consumer_id
			) {
				setIsSeller(false)
				setIsWalletSeller(true)

				// On regarde si l'operateur du velo vendu ("account_id") correspond à l'operateur du magasin en cours
				// Si oui, on affiche les infos vélos
			} else if (getBike.account_id === operatorAccountID) {
				const canSellBike =
					bikeStatus !== BIKE_STOLEN && PropertiyrequestStatusBike !== REQUEST_ONGOING
				setIsWalletSeller(canSellBike)
				setIsSeller(false)
			} else if (
				getBike.bike_status === BIKE_BACK_STORE &&
				(bikeStoreId === ARCADE_STOREID ||
					bikeStoreId === ARCADE_STOREID_ST ||
					(isMFCIntersportSeller && isMFCBike))
			) {
				//@TODO - attente API Mounir qui va nous retourner is_arcade
				// pour l'instant on se base sur le store_id arcade
				setIsSeller(true)
			} else if (bikeStoreId !== storeStoreId && bikeStoreId !== oldStoreId) {
				setIsSeller(false)
			}
		}

		return {
			...loadedBikeBasis,
			...loadedBikeDescription,
			...loadedBikeSoldInfos,
		}
	}

	return (
		<BikeStoreContext.Provider
			value={{
				bikeProcessState,
				bikeDispatch,

				setInitialBike,
				setCurrentBike,
				setShowOwner,
				resetBikeStoreInfo,

				fetchBike,
			}}
		>
			{children}
		</BikeStoreContext.Provider>
	)
}

/**********************
 ** Context hook **
 **********************/

const useBikeStoreProcess = () => {
	const context = React.useContext(BikeStoreContext)
	if (context === undefined) {
		throw new Error("useBikeStoreProcess must be used within a BikeStoreContext")
	}
	return context
}

export { useBikeStoreProcess, BikeStoreProvider }
